import { Accordion } from "../global";

export class VariantRadios extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
    this._onPopState = this.onPopState.bind(this);
    this.productForm = document.getElementById('oli-product-form');
  }

  connectedCallback() {
    window.addEventListener('popstate', this._onPopState);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this._onPopState);
  }

  /**
   * When the URL changes via browser back/forward (same product path, different ?variant=),
   * Barba intentionally skips a full page swap; re-fetch partial PDP markup from the URL.
   */
  onPopState() {
    if (this.dataset.updateUrl === 'false') {
      return;
    }
    try {
      const loc = new URL(window.location.href);
      const productPath = new URL(this.dataset.url, window.location.href).pathname;
      if (loc.pathname !== productPath) {
        return;
      }
      const variantParam = loc.searchParams.get('variant');
      if (!variantParam) {
        return;
      }
      const variantId = Number.parseInt(variantParam, 10);
      const variant = this.getVariantData().find((v) => v.id === variantId);
      if (!variant) {
        return;
      }

      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      variant.options.forEach((value, index) => {
        const fieldset = fieldsets[index];
        if (!fieldset) {
          return;
        }
        const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
        const match =
          radios.find((inp) => inp.value === value && !inp.disabled) ||
          radios.find((inp) => inp.value === value);
        if (match && !match.checked) {
          match.checked = true;
        }
      });

      this.currentVariant = variant;
      this.updateOptions();
      this.updatePackLegendValue();
      this.renderProductInfo();
    } catch {
      /* ignore */
    }
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.updatePackLegendValue();

    if (!this.currentVariant) {
      this.setUnavailable();
    } else {
      this.updateURL();
      this.renderProductInfo();
    }
  }

  /**
   * Keeps the inline "Pack: …" label in sync with the selected size option.
   */
  updatePackLegendValue() {
    const fieldset =
      this.querySelector('.product-form__fieldset--size') ||
      this.querySelector('.product-form__fieldset--pack-option');
    const checkedInput = fieldset?.querySelector('input[type="radio"]:checked');
    if (!checkedInput) return;

    const legendValue = fieldset.querySelector('[data-pack-legend-value]');
    if (legendValue) {
      legendValue.textContent = checkedInput.value;
    }

    const varietyPackLegend = document.querySelector('[data-variety-pack-legend-value]');
    if (varietyPackLegend) {
      const prefix = checkedInput.value.trim().split(/\s+/)[0] || checkedInput.value.trim();
      varietyPackLegend.textContent = prefix ? `${prefix} pack` : checkedInput.value;
    }
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({ }, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  renderProductInfo() {
    fetch(`${this.dataset.url}?variant=${ this.currentVariant.id}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const changeSelectors = [
          `#product-form-retailers-check`,
          `#product-featured-image-oli-product-gallery`,
          `#product-ingredients-section`,
          `#product-size-description`,
          `#product-selling-plan-selector`,
          `[data-submit-button-text]`,
          `#product-form-retailers`,
          `#product-form-beam-widget`
        ];

        changeSelectors.forEach(selector => {
          const destination = document.querySelector(selector);
          const source = html.querySelector(selector);
          if (destination && source) {
            if (selector === '#product-selling-plan-selector') {
              destination.outerHTML = source.outerHTML;
            } else {
              destination.innerHTML = source.innerHTML;
            }
          }
        });

        const fsSourcesDest = document.querySelector('[data-product-gallery-fs-sources]');
        const fsSourcesSrc = html.querySelector('[data-product-gallery-fs-sources]');
        if (fsSourcesDest && fsSourcesSrc) {
          fsSourcesDest.textContent = fsSourcesSrc.textContent;
        }

        document.querySelector('.keen-slider-container')?.dispatchEvent(
          new CustomEvent('oli:product-gallery-fs-sources-updated')
        );

        this.reinitializeSellingPlan();

        const accordions = document.querySelectorAll('accordion-group');

        if (accordions.length) {
          accordions.forEach(accordion => {
            // eslint-disable-next-line no-new
            new Accordion(accordion);
          });
        }

        // check if it retailer variant
        const retailerVariantInput = document.querySelector('#product-form-retailers-input');

        if (retailerVariantInput) {
          document.querySelector('#product-form-content')?.classList.add('hide');
          document.querySelector('[data-sticky-add-to-cart]')?.classList.add('hide');
          document.body.classList.add('is-retailer-variant');
        } else {
          document.querySelector('#product-form-content:not(.hide-dtc)')?.classList.remove('hide');
          document.querySelector('[data-sticky-add-to-cart]')?.classList.remove('hide');
          document.body.classList.remove('is-retailer-variant');
        }

        const stickyMarkup = html.querySelector('[data-sticky-add-to-cart]')?.outerHTML;

        if (stickyMarkup) {
          window.dispatchEvent(new CustomEvent('oli:sticky-add-to-cart:update', {
            detail: {
              markup: stickyMarkup,
            },
          }));
        }

      });
  }

  reinitializeSellingPlan() {
    const subscriptionRoot = document.querySelector('[id^="rtx_widget_injection_parent_"][data-product-id]');
    if (!subscriptionRoot) return;

    if (window.setSellingPlan && subscriptionRoot.dataset.productId) {
      const productId = subscriptionRoot.dataset.productId;
      window.setSellingPlan(productId, true);
      requestAnimationFrame(() => {
        window.setSellingPlan(productId, true);
        requestAnimationFrame(() => {
          window.setSellingPlan(productId, true);
        });
      });
      window.setTimeout(() => {
        window.setSellingPlan(productId, true);
      }, 120);
    }
  }


  setUnavailable() {
    const addButton = this.productForm.querySelector('[name="add"]');
    const addButtonText = this.productForm.querySelector('[name="add"] > span');

    if (!addButton) return;
    addButton.setAttribute('disabled', 'disabled')
    addButtonText.textContent = theme.strings.unavailable;
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (!customElements.get('variant-radios')) {
    customElements.define('variant-radios', VariantRadios);
  }
})